import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Details } from '@digdir/designsystemet-react';

import { SummaryCard } from '../../../../expertRegistration/json/common/summary/styledComponents';
import { useGetUserInputReadOnly } from '../../../../registration/hooks/useGetUserInput';
import { ErrorState } from '../../../components/errorState';
import { PageLoader } from '../../../components/pageLoader';
import { useUserInputValidation } from '../hooks/useUserInputValidation';
import ShapeAndDirectionSummarySection from '../../../../registration/components/summary/tabs/review/shapeAndDirectionSummarySection';
import WallsSummarySection from '../../../../registration/components/summary/tabs/review/wallsSummarySection';
import WindowsSummarySection from '../../../../registration/components/summary/tabs/review/windowsSummarySection';
import { Himmelretning } from '../../../../../components/registering/utils/registerEnums';
import ReadonlyField from '../../../../../components/readonlyField';
import RoofSummarySection from '../../../../registration/components/summary/tabs/review/roofSummarySection';
import FloorsSummarySection from '../../../../registration/components/summary/tabs/review/floorsSummarySection';
import DoorsSummarySection from '../../../../registration/components/summary/tabs/review/doorsSummarySection';
import { AreaOfExpertise } from '../../../../../types/user';
import { SummaryResult } from '../result';
import { userHasBygningsekspertRole } from '../../../utils/stepperUtils';
import { useUser } from '../../../../../hooks/useUser';
import { EnergyPlan } from '../../../../../types/building';
import { TiltakView } from '../tiltak';
import { Tiltak } from '../../../../../types/registration/tiltak';

import { BoligInformasjon } from './boligInformasjon';
import { VentilasjonView } from './ventilasjon';
import { TekniskUtstyrView } from './oppvarming';
import { KonstruksjonstetthetView } from './konstruksjonstetthet';
import { EgenprodusertElektrisitetView } from './egenprodusertElektrisitet';

interface SummaryReviewProps {
  registrationId: string;
  energiplan: EnergyPlan;
  tiltakList: Tiltak[] | undefined;
}
// Some of this component has be changed to fit the new flow
// TODO: Rewrite the remaining components to also use Designsystemet
export const SummaryReview: FC<SummaryReviewProps> = ({ registrationId, energiplan, tiltakList }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { userInput, userInputLoading, userInputError } = useGetUserInputReadOnly(registrationId);
  const { hasDoors, hasFloorData, hasShapeAndDirectionData, hasRoofData, hasWallData, hasWindows } =
    useUserInputValidation(userInput);
  const terrengskjerming = userInput?.bygningsdetaljer.terrengskjerming;
  const konstruksjonstetthet = userInput?.bygningsdetaljer.konstruksjonstetthet;

  const utdata = energiplan.energimerke?.utdata;
  const energiforbruk = utdata?.energiforbruk;
  const solarInstrumentation = utdata?.solarInstrumentation;
  const hasInstrumentationKeys =
    Boolean(solarInstrumentation && Object.keys(solarInstrumentation).length > 0);
  const isBygningsekspert = userHasBygningsekspertRole(user);
  const hasEgenprodusertElektrisitet =
    (energiforbruk?.egenprodusertElektrisitetTilEgenbruk ?? 0) > 0 ||
    (energiforbruk?.egenprodusertElektrisitetTilEksport ?? 0) > 0 ||
    (hasInstrumentationKeys && isBygningsekspert);

  if (userInputLoading) {
    return <PageLoader />;
  }

  if (userInputError) {
    return <ErrorState />;
  }

  if (!userInput || !userInput.tekniskUtstyr || !userInput.ventilasjon) {
    return null; // Return an error page/message when this happens
  }

  return (
    <SummaryCard>
      <Details>
        <Details.Summary>{t('expertToolsJSON.summary.boliginformasjon.title')}</Details.Summary>
        <Details.Content>
          <BoligInformasjon data={userInput.bygningsdetaljer} />
        </Details.Content>
      </Details>
      <Details>
        <Details.Summary>{t('register.tekniskUtstyr.oppvarming.title')}</Details.Summary>
        <Details.Content>
          <TekniskUtstyrView data={userInput.tekniskUtstyr} />
        </Details.Content>
      </Details>
      <Details>
        <Details.Summary>{t('register.ventilation.title')}</Details.Summary>
        <Details.Content>
          <VentilasjonView data={userInput.ventilasjon} />
        </Details.Content>
      </Details>

      {hasShapeAndDirectionData && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.bygningsform.title')}</Details.Summary>
          <Details.Content>
            <ShapeAndDirectionSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {hasWallData && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.vegger.title')}</Details.Summary>
          <Details.Content>
            <WallsSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {hasWindows && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.vinduer.title')}</Details.Summary>
          <Details.Content>
            <WindowsSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {terrengskjerming && (
        <Details>
          <Details.Summary>{t('register.terrainShielding.title')}</Details.Summary>
          <Details.Content>
            {Object.values(Himmelretning).map((himmelretning) => (
              <ReadonlyField
                key={himmelretning}
                label={t(`direction.${himmelretning}`)}
                value={
                  terrengskjerming[himmelretning]
                    ? t(`register.terrainShielding.option.${terrengskjerming[himmelretning]}`)
                    : t('register.missingValue')
                }
              />
            ))}
          </Details.Content>
        </Details>
      )}
      {hasRoofData && (
        <Details>
          <Details.Summary>{t('register.roof.title')}</Details.Summary>
          <Details.Content>
            <RoofSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {hasFloorData && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.gulv.title')}</Details.Summary>
          <Details.Content>
            <FloorsSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {hasDoors && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.dorer.title')}</Details.Summary>
          <Details.Content>
            <DoorsSummarySection expandedFactBox={true} />
          </Details.Content>
        </Details>
      )}
      {konstruksjonstetthet && (
        <Details>
          <Details.Summary>{t('register.constructionDensity.title')}</Details.Summary>
          <Details.Content>
            <KonstruksjonstetthetView tetthet={konstruksjonstetthet} />
          </Details.Content>
        </Details>
      )}
      {hasEgenprodusertElektrisitet && (
        <Details>
          <Details.Summary>{t('expertToolsJSON.summary.solceller.heading')}</Details.Summary>
          <Details.Content>
            <EgenprodusertElektrisitetView energiplan={energiplan} />
          </Details.Content>
        </Details>
      )}
      {(user?.fagomrader.includes(AreaOfExpertise.BYGNINGSEKSPERT) ||
        user?.fagomrader.includes(AreaOfExpertise.ENERGIRADGIVER)) && (
        <SummaryResult energiplan={energiplan} userInput={userInput} />
      )}
      {tiltakList && tiltakList.length > 0 && <TiltakView tiltakList={tiltakList} />}
    </SummaryCard>
  );
};
